/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  DiagnosticFirmwareInitialProperties,
  DiagnosticFirmwareRequest,
  DiagnosticFirmwareResponse,
} from '../../interface/diagnostic-firmware.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import diagnosticFirmwareApi from '../../utils/api/diagnostic-firmware';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Thunk to fetch diagnostic firmware list.
 * @param {DiagnosticFirmwareRequest} data - The parameters for fetching diagnostic firmware.
 * @param {any} thunkAPI - The thunk API object containing dispatch.
 * @returns {Promise<DiagnosticFirmwareResponse>} Resolves with the diagnostic firmware response data.
 */
export const fetchDiagnosticFirmware = createAsyncThunk<
  DiagnosticFirmwareResponse,
  DiagnosticFirmwareRequest
>(
  'diagnostic-firmware/fetchDiagnosticFirmware',
  async (data: DiagnosticFirmwareRequest, thunkAPI) => {
    const { dispatch } = thunkAPI;
    try {
      const serialNumber = data.chargingPointSn || data.chargingpointsn;
      const targetId = data.id || data.deviceId;
      const queryData: Record<string, any> = { ...data };
      delete queryData.chargingpointsn;
      delete queryData.chargingPointSn;
      delete queryData.id;
      delete queryData.deviceId;

      if (serialNumber) {
        queryData.chargingPointSn = Array.isArray(serialNumber) ? serialNumber[0] : serialNumber;
      }

      const queryParameters = removeEmptyKeys(queryData);
      const response = await diagnosticFirmwareApi.fetchDiagnosticFirmwareList({
        id: targetId,
        ...queryParameters,
      });

      let items: any[] = [];
      let totalResults = 0;
      let totalPages = 1;
      let limit = data.limit || DefaultApiLimit.limit;
      let page = data.page || DefaultApiLimit.page;

      if (Array.isArray(response.data)) {
        items = response.data;
        totalResults = items.length;
        totalPages = Math.ceil(totalResults / limit) || 1;
      } else if (response.data && Array.isArray(response.data.results)) {
        items = response.data.results;
        totalResults = response.data.totalResults ?? items.length;
        totalPages = response.data.totalPages ?? Math.ceil(totalResults / limit) ?? 1;
        limit = response.data.limit ?? limit;
        page = response.data.page ?? page;
      } else if (response.data && Array.isArray(response.data.data)) {
        items = response.data.data;
        totalResults = response.data.totalResults ?? items.length;
        totalPages = response.data.totalPages ?? Math.ceil(totalResults / limit) ?? 1;
      } else if (
        response.data &&
        (response.data._id || response.data.id || response.data.chargingPointSn)
      ) {
        items = [response.data];
        totalResults = 1;
        totalPages = 1;
      }

      return {
        limit,
        page,
        totalPages,
        totalResults,
        results: items.map((item: any) => ({
          ...item,
          id: item._id || item.id,
          date: ConvertUTCtoDate(item.createdAt || item.sentAt || item.startTime),
          time: FormatUtcString(ConvertUTCtoHHMM(item.createdAt || item.sentAt || item.startTime)),
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to refresh diagnostic firmware list without error notifications.
 * @param {DiagnosticFirmwareRequest} data - The parameters for refreshing diagnostic firmware.
 * @returns {Promise<DiagnosticFirmwareResponse>} Resolves with the refreshed diagnostic firmware response data.
 */
export const refreshDiagnosticFirmware = createAsyncThunk<
  DiagnosticFirmwareResponse,
  DiagnosticFirmwareRequest
>(
  'diagnostic-firmware/refreshDiagnosticFirmware',
  async (data: DiagnosticFirmwareRequest) => {
    const serialNumber = data.chargingPointSn || data.chargingpointsn;
    const targetId = data.id || data.deviceId;
    const queryData: Record<string, any> = { ...data };
    delete queryData.chargingpointsn;
    delete queryData.chargingPointSn;
    delete queryData.id;
    delete queryData.deviceId;

    if (serialNumber) {
      queryData.chargingPointSn = Array.isArray(serialNumber) ? serialNumber[0] : serialNumber;
    }

    const queryParameters = removeEmptyKeys(queryData);
    const response = await diagnosticFirmwareApi.fetchDiagnosticFirmwareList({
      id: targetId,
      ...queryParameters,
    });

    let items: any[] = [];
    let totalResults = 0;
    let totalPages = 1;
    let limit = data.limit || DefaultApiLimit.limit;
    let page = data.page || DefaultApiLimit.page;

    if (Array.isArray(response.data)) {
      items = response.data;
      totalResults = items.length;
      totalPages = Math.ceil(totalResults / limit) || 1;
    } else if (response.data && Array.isArray(response.data.results)) {
      items = response.data.results;
      totalResults = response.data.totalResults ?? items.length;
      totalPages = response.data.totalPages ?? Math.ceil(totalResults / limit) ?? 1;
      limit = response.data.limit ?? limit;
      page = response.data.page ?? page;
    } else if (response.data && Array.isArray(response.data.data)) {
      items = response.data.data;
      totalResults = response.data.totalResults ?? items.length;
      totalPages = response.data.totalPages ?? Math.ceil(totalResults / limit) ?? 1;
    } else if (
      response.data &&
      (response.data._id || response.data.id || response.data.chargingPointSn)
    ) {
      items = [response.data];
      totalResults = 1;
      totalPages = 1;
    }

    return {
      ...response.data,
      limit,
      page,
      totalPages,
      totalResults,
      results: items.map((item: any) => ({
        ...item,
        id: item._id || item.id,
        date: ConvertUTCtoDate(item.createdAt || item.sentAt || item.startTime),
        time: FormatUtcString(ConvertUTCtoHHMM(item.createdAt || item.sentAt || item.startTime)),
      })),
    };
  },
);

/**
 * Thunk to fetch diagnostic firmware list filters.
 * @returns {Promise<CommonFilterProperties[]>} Resolves with array of filter properties.
 */
export const fetchDiagnosticFirmwareFilters = createAsyncThunk<
  CommonFilterProperties[],
  { chargingPointSn?: string } | undefined
>('diagnostic-firmware/fetchDiagnosticFirmwareFilters', async () => {
  try {
    const response = await diagnosticFirmwareApi.fetchDiagnosticFirmwareFilters();
    if (!response?.data) return [];

    if (
      Array.isArray(response.data) &&
      response.data.length > 0 &&
      response.data[0]?.subFilters
    ) {
      return ConfigureListFilterResponse(response.data);
    }

    const items: any[] = Array.isArray(response.data)
      ? response.data
      : response.data?.results || response.data?.data || (response.data?._id ? [response.data] : []);

    const dynamicFilters: CommonFilterProperties[] = [];

    const chargingPoints = [
      ...new Set(items.map((item: any) => item.chargingPointSn).filter(Boolean)),
    ];
    if (chargingPoints.length > 0) {
      dynamicFilters.push({
        name: 'Charging Point',
        key: 'chargingPointSn',
        type: filterType.MULTISELECT,
        value: '',
        listOfValue: chargingPoints.map((serialNumber: string) => ({
          label: serialNumber,
          value: serialNumber,
        })),
        applyFilter: false,
        enable: true,
      });
    }

    const statuses = [...new Set(items.map((item: any) => item.status).filter(Boolean))];
    if (statuses.length > 0) {
      dynamicFilters.push({
        name: 'Status',
        key: 'status',
        type: filterType.MULTISELECT,
        value: '',
        listOfValue: statuses.map((status: string) => ({
          label: status,
          value: status,
        })),
        applyFilter: false,
        enable: true,
      });
    }

    const domains = [...new Set(items.map((item: any) => item.domain).filter(Boolean))];
    if (domains.length > 0) {
      dynamicFilters.push({
        name: 'Domain',
        key: 'domain',
        type: filterType.MULTISELECT,
        value: '',
        listOfValue: domains.map((domain: string) => ({
          label: domain,
          value: domain,
        })),
        applyFilter: false,
        enable: true,
      });
    }

    return dynamicFilters;
  } catch {
    return [];
  }
});

const defaultFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: DiagnosticFirmwareInitialProperties = {
  diagnostics: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: defaultFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for managing diagnostic firmware state.
 */
export const diagnosticFirmwareSlice = createSlice({
  name: 'diagnostic-firmware',
  initialState,
  reducers: {
    /**
     * Update diagnostic firmware filters.
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateDiagnosticFirmwareFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.sortBy = null;
        state.sortField = null;
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to reset isFilterUpdated status.
     * @param {any} state - The current slice state.
     */
    disableDiagnosticFirmwareFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Function to set sortBy and sortField.
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Action reducer map builder for asynchronous thunks.
   * @param {any} builder - The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchDiagnosticFirmware.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDiagnosticFirmware.fulfilled, (state, action) => {
        state.loading = false;
        state.diagnostics = [...(action.payload?.results || [])];
        state.limit = action.payload?.limit || DefaultApiLimit.limit;
        state.page = action.payload?.page || DefaultApiLimit.page;
        state.totalPages = action.payload?.totalPages || 0;
        state.totalResults = action.payload?.totalResults || 0;
      })
      .addCase(fetchDiagnosticFirmware.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(refreshDiagnosticFirmware.fulfilled, (state, action) => {
      state.diagnostics = [...(action.payload?.results || [])];
      state.limit = action.payload?.limit || DefaultApiLimit.limit;
      state.page = action.payload?.page || DefaultApiLimit.page;
      state.totalPages = action.payload?.totalPages || 0;
      state.totalResults = action.payload?.totalResults || 0;
    });
    builder
      .addCase(fetchDiagnosticFirmwareFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchDiagnosticFirmwareFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...defaultFilter, ...action.payload];
        },
      )
      .addCase(fetchDiagnosticFirmwareFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updateDiagnosticFirmwareFilters,
  disableDiagnosticFirmwareFiltersApplied,
  setSortData,
} = diagnosticFirmwareSlice.actions;

export default diagnosticFirmwareSlice.reducer;
